/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {CSSProperties, useCallback, useState} from 'react';
import {Grid, Theme} from '@material-ui/core';
import Button, {ButtonProps} from '@material-ui/core/Button';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {IconButtonDirectionEnum} from '@/types/';
import {OMIT_CLICK_AWAY_LISTENER} from '../../maps/constants';

export interface TertiaryIconButtonProps extends Omit<ButtonProps, 'onClick' | 'id'> {
  icon?: JSX.Element;
  label?: string;
  iconDirection?: keyof typeof IconButtonDirectionEnum;
  labelwrapper?: string;
  large?: boolean;
  margintop?: number;
  lineheight?: string;
  maxwidth?: string;
  root?: {
    height?: string;
  };
  labelStyle?: CSSProperties;
  pressedEffect?: boolean;
  isGeofence?: boolean;
  id?: number;
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, id?: number) => void;
}

const getLabelPropValue = (props: TertiaryIconButtonProps, name: string, stackedValue: any) => {
  if (props) {
    if (props[name]) {
      return props[name];
    } else if (!props.iconDirection || props.iconDirection === 'stack') {
      return stackedValue;
    }
  }
  return undefined;
};

const useStyles = makeStyles((theme: Theme) => ({
  root : {
    color         : dlsColor('aaBlue'),
    fontWeight    : 'bold',
    textTransform : 'uppercase',
    fontSize      : ({large}: {large: boolean}) =>
      !large ? dlsAlt('typography', 'btn', 'smallFontSize') : theme.typography.fontSize,
    letterSpacing : dlsAlt('typography', 'btn', 'letterSpacing'),
    '&:hover'     : {
      textDecoration  : 'underline',
      backgroundColor : ({pressedEffect}: {pressedEffect: boolean}) => pressedEffect
        ? dlsAlt('components', 'icon', 'selected') : 'transparent'
    },
    '&:active' : {
      color : dlsColor('grey1')
    },
    '&:disabled' : {
      color : dlsAlt('components', 'btn', 'disabledColor')
    },
    height : (props: {root: {height: string}}) => props?.root?.height
  },
  labelWrapper : (props: {
    labelwrapper?: {
      height?: string;
      alignItems?: string;
    }
  }) => props?.labelwrapper,
  disabled : {},
  label    : {
    marginRight : 6,
    marginLeft  : 6,
    marginTop   : (props) => getLabelPropValue(props, 'margintop', 3),
    lineHeight  : (props) => getLabelPropValue(props, 'lineheight', 'normal'),
    maxWidth    : (props: {maxwidth: string}) => props?.maxwidth
  }
}));

/**
 *
 * @TertiaryIconButton
 *
 * Tertiary buttons can be paired with primary buttons, secondary buttons,
 * or other tertiary buttons with Left, Right & Stack Icons. By Default
 * Icons direction is 'Stack'
 *
 * ```typescript
 *
 * import React from 'react';
 * import { TertiaryIconButton } from '@ion/dls/src/components/buttons/';
 * import { SystemContactIcon } from '@ion/dls/src/icons/';
 *
 * function MyComponent {
 *   return (
 *     <React.Fragment>
 *       <TertiaryIconButton iconDirection='left'
 *         icon={
 *           <SystemContactIcon size={IconSize.medium} />
 *          }
 *          label='Contact' />
 *        <br />
 *       <TertiaryIconButton label='Contact'
 *         icon={
 *           <SystemContactIcon size={IconSize.medium} />
 *          } />
 *     </React.Fragment>
 *   );
 * }
 *
 * ```
 *
 */
const TertiaryIconButton = (props: TertiaryIconButtonProps) => {
  const {
    icon,
    label,
    large,
    pressedEffect,
    iconDirection = 'stack',
    labelStyle,
    isGeofence,
    id,
    onClick,
    ...buttonProps
  } = props;
  const classes = useStyles(props);
  const [isMouseDown, setIsMouseDown] = useState(false);

  const _onClick = useCallback(function(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
    onClick?.(e, id);
  }, [onClick, id]);

  return (
    <Button disableRipple
      classes={{
        root  : classes.root,
        label : classes.labelWrapper
      }}
      onMouseDown={function(evt) {
        setIsMouseDown(true);
        if (props.onMouseDown) {
          props.onMouseDown(evt);
        }
      }}
      onMouseUp={function(evt) {
        setIsMouseDown(false);
        if (props.onMouseUp) {
          props.onMouseUp(evt);
        }
      }}
      onClick={_onClick}
      {...buttonProps}>
      <Grid container
        direction={
          iconDirection === 'stack'
            ? 'column'
            : iconDirection === 'right'
              ? 'row-reverse'
              : 'row'
        }
        justifyContent='center'
        alignItems='center'>
        {React.cloneElement(icon, {
          color : props.disabled
            ? dlsColor('grey4')
            : isMouseDown
              ? dlsColor('grey1')
              : dlsColor('caBlue')
        })}
        <span className={classnames(classes.label,
          isGeofence ? OMIT_CLICK_AWAY_LISTENER : '')}
        style={{
          color : props.disabled
            ? dlsColor('grey4')
            : isMouseDown
              ? dlsColor('grey1')
              : dlsColor('caBlue'),
          ...labelStyle
        }}>
          {label}
        </span>
      </Grid>
    </Button>
  );
};

export {TertiaryIconButton};
